import React, { useEffect, useState } from "react";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, Phone, MessageSquare, CalendarCheck } from "lucide-react";
import { Toaster } from "sonner";
import { Header } from "@/components/site/Header";
import { Hero } from "@/components/site/Hero";
import { SchoolMap } from "@/components/site/SchoolMap";
import { Contact } from "@/components/site/Contact";
import { Footer } from "@/components/site/Footer";
import { EyeCursor } from "@/components/site/ui";
import { scrollToId } from "@/lib/scroll";
import { CONTACT } from "@/config/content";
import {
  AudienceStrip, SchoolTesting, Benefits, HowItWorks, AssessmentCovers, Services,
  Therapy, OrthoVsOpto, WhoWeHelp, Reporting, Impact, Testimonials, Gallery, Marquee, FAQ, FinalCTA, VisionLearning, FreeBanner, StatsBar, GlassesImpact, SectorStrip, Availability, Credentials, FrameRange, ProgramInsights, VideoFeature,
} from "@/components/site/Sections";

export default function Home() {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="relative">
      <EyeCursor />
      <Toaster position="top-center" richColors />
      <Header />

      <main>
        <Hero />
        <VideoFeature />
        <StatsBar />
        <FreeBanner />
        <AudienceStrip />
        <SectorStrip />
        <SchoolTesting />
        <VisionLearning />
        <Benefits />
        <HowItWorks />
        <Availability />
        <AssessmentCovers />
        <Reporting />
        <Services />
        <Marquee />
        <Therapy />
        <OrthoVsOpto />
        <WhoWeHelp />
        <Impact />
        <ProgramInsights />
        <GlassesImpact />
        <FrameRange />
        <SchoolMap />
        <Testimonials />
        <Gallery />
        <FAQ />
        <Credentials />
        <FinalCTA />
        <Contact />
      </main>

      <Footer />

      {/* Sticky desktop book button */}
      <button onClick={() => scrollToId("contact")} data-testid="sticky-book-btn"
        className="hidden lg:flex fixed right-6 bottom-6 z-40 items-center gap-2 rounded-full bg-brand-teal hover:bg-brand-tealdark text-white font-semibold px-5 py-3.5 shadow-xl shadow-brand-teal/30 transition-colors focus-ring">
        <CalendarCheck className="w-4 h-4" /> Book Your School Visit
      </button>

      {/* Back to top */}
      <AnimatePresence>
        {showTop && (
          <motion.button initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="back-to-top"
            className="hidden lg:grid place-items-center fixed left-6 bottom-6 z-40 w-11 h-11 rounded-full bg-white text-brand-navy border border-brand-navy/10 soft-shadow hover:border-brand-teal focus-ring" aria-label="Back to top">
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Mobile bottom bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 grid grid-cols-2 gap-px bg-brand-navy/10 glass border-t border-brand-navy/10">
        <a href={`tel:${CONTACT.phoneIntl}`} data-testid="mobilebar-call" className="flex items-center justify-center gap-2 py-3.5 font-semibold text-brand-navy focus-ring"><Phone className="w-4 h-4 text-brand-teal" /> Call</a>
        <button onClick={() => scrollToId("contact")} data-testid="mobilebar-enquire" className="flex items-center justify-center gap-2 py-3.5 font-semibold text-white bg-brand-teal focus-ring"><MessageSquare className="w-4 h-4" /> Enquire</button>
      </div>
    </div>
  );
}
